// Static interface copy in every supported app language. User and model content is not translated here.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  draftTitle: 'Draft v{n}',
  versionTitle: 'v{n}',
  notPublished: 'Not published',
  published: 'Published',
  publish: 'Publish',
  restore: 'Restore…',
  basedOn: 'based on v{n}',
  builtBy: 'by the builder, for {who}',
  publishedBy: 'published by {who}, {at}',
  builtWith: 'Built with {model}',
  openRun: 'Open run',
  compareTitle: 'Compare draft v{to} with v{from}',
  compareHint: 'Drag to compare',
  compareLabel: 'Compare v{from} and v{to}',
  compareFrom: 'v{n}',
  compareTo: 'v{n}',
  empty: 'No versions yet.',
  noPreview: 'No preview was saved for this version.',
  viewingEarlier: 'Viewing v{n}. This is not the version people use.',
  // An App save refused for its tables (app_schema_change_*), said once under the failed step.
  saveRefusedRows:
    'Saved rows don’t fit this table change, so this version wasn’t saved. Your rows are unchanged.',
  saveTablesPending:
    'This version is saved, but its tables haven’t changed yet. They change the next time it’s saved.',
};

export type AppVersionsCopyKey = keyof typeof en;

export const appVersionsCopy: NamespaceCatalog<keyof typeof en> = {
  en: {
    draftTitle: 'Draft v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Not published',
    published: 'Published',
    publish: 'Publish',
    restore: 'Restore…',
    basedOn: 'based on v{n}',
    builtBy: 'by the builder, for {who}',
    publishedBy: 'published by {who}, {at}',
    builtWith: 'Built with {model}',
    openRun: 'Open run',
    compareTitle: 'Compare draft v{to} with v{from}',
    compareHint: 'Drag to compare',
    compareLabel: 'Compare v{from} and v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'No versions yet.',
    noPreview: 'No preview was saved for this version.',
    viewingEarlier: 'Viewing v{n}. This is not the version people use.',
    saveRefusedRows:
      'Saved rows don’t fit this table change, so this version wasn’t saved. Your rows are unchanged.',
    saveTablesPending:
      'This version is saved, but its tables haven’t changed yet. They change the next time it’s saved.',
  },
  bg: {
    draftTitle: 'Чернова v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Непубликувана',
    published: 'Публикувана',
    publish: 'Публикувай',
    restore: 'Възстанови…',
    basedOn: 'на база v{n}',
    builtBy: 'от създателя, за {who}',
    publishedBy: 'публикувана от {who}, {at}',
    builtWith: 'Създадена с {model}',
    openRun: 'Отвори изпълнението',
    compareTitle: 'Сравняване на чернова v{to} с v{from}',
    compareHint: 'Плъзнете, за да сравните',
    compareLabel: 'Сравни v{from} и v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Все още няма версии.',
    noPreview: 'За тази версия не е запазен преглед.',
    viewingEarlier: 'Преглеждате v{n}. Това не е версията, която хората използват.',
    saveRefusedRows:
      'Запазените редове не пасват на тази промяна в таблицата, затова тази версия не беше запазена. Редовете ви са непроменени.',
    saveTablesPending:
      'Тази версия е запазена, но таблиците ѝ още не са променени. Ще се променят при следващото ѝ запазване.',
  },
  cs: {
    draftTitle: 'Koncept v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Nevydáno',
    published: 'Vydáno',
    publish: 'Vydat',
    restore: 'Obnovit…',
    basedOn: 'založeno na v{n}',
    builtBy: 'vytvořil tvůrce pro {who}',
    publishedBy: 'vydal {who}, {at}',
    builtWith: 'Vytvořeno pomocí {model}',
    openRun: 'Otevřít spuštění',
    compareTitle: 'Porovnat koncept v{to} s v{from}',
    compareHint: 'Porovnávejte přetažením',
    compareLabel: 'Porovnat v{from} a v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Zatím nejsou k dispozici žádné verze.',
    noPreview: 'Pro tuto verzi nebyl uložen žádný náhled.',
    viewingEarlier: 'Prohlížíte v{n}. Tuto verzi lidé nepoužívají.',
    saveRefusedRows:
      'Uložené řádky neodpovídají této změně tabulky, proto se tato verze neuložila. Vaše řádky zůstaly beze změny.',
    saveTablesPending:
      'Tato verze je uložena, ale její tabulky se zatím nezměnily. Změní se při dalším uložení.',
  },
  da: {
    draftTitle: 'Kladde v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Ikke udgivet',
    published: 'Udgivet',
    publish: 'Udgiv',
    restore: 'Gendan…',
    basedOn: 'baseret på v{n}',
    builtBy: 'bygget af den, der byggede den, til {who}',
    publishedBy: 'udgivet af {who}, {at}',
    builtWith: 'Bygget med {model}',
    openRun: 'Åbn kørsel',
    compareTitle: 'Sammenlign kladde v{to} med v{from}',
    compareHint: 'Træk for at sammenligne',
    compareLabel: 'Sammenlign v{from} og v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Der er ingen versioner endnu.',
    noPreview: 'Der blev ikke gemt nogen forhåndsvisning af denne version.',
    viewingEarlier: 'Viser v{n}. Det er ikke den version, folk bruger.',
    saveRefusedRows:
      'Gemte rækker passer ikke til denne tabelændring, så denne version blev ikke gemt. Dine rækker er uændrede.',
    saveTablesPending:
      'Denne version er gemt, men dens tabeller er ikke ændret endnu. De ændres, næste gang den gemmes.',
  },
  de: {
    draftTitle: 'Entwurf v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Nicht veröffentlicht',
    published: 'Veröffentlicht',
    publish: 'Veröffentlichen',
    restore: 'Wiederherstellen…',
    basedOn: 'basierend auf v{n}',
    builtBy: 'vom Builder für {who} erstellt',
    publishedBy: 'von {who} veröffentlicht, {at}',
    builtWith: 'Erstellt mit {model}',
    openRun: 'Ausführung öffnen',
    compareTitle: 'Entwurf v{to} mit v{from} vergleichen',
    compareHint: 'Zum Vergleichen ziehen',
    compareLabel: 'v{from} und v{to} vergleichen',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Noch keine Versionen.',
    noPreview: 'Für diese Version wurde keine Vorschau gespeichert.',
    viewingEarlier: 'Sie sehen v{n}. Dies ist nicht die Version, die andere verwenden.',
    saveRefusedRows:
      'Gespeicherte Zeilen passen nicht zu dieser Tabellenänderung, daher wurde diese Version nicht gespeichert. Ihre Zeilen sind unverändert.',
    saveTablesPending:
      'Diese Version ist gespeichert, aber ihre Tabellen haben sich noch nicht geändert. Sie ändern sich beim nächsten Speichern.',
  },
  el: {
    draftTitle: 'Πρόχειρο v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Δεν έχει δημοσιευτεί',
    published: 'Δημοσιευμένο',
    publish: 'Δημοσίευση',
    restore: 'Επαναφορά…',
    basedOn: 'με βάση το v{n}',
    builtBy: 'από τον δημιουργό, για τον/την {who}',
    publishedBy: 'δημοσιεύτηκε από τον/την {who}, {at}',
    builtWith: 'Δημιουργήθηκε με {model}',
    openRun: 'Άνοιγμα εκτέλεσης',
    compareTitle: 'Σύγκριση του πρόχειρου v{to} με το v{from}',
    compareHint: 'Σύρετε για σύγκριση',
    compareLabel: 'Σύγκριση των v{from} και v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Δεν υπάρχουν ακόμη εκδόσεις.',
    noPreview: 'Δεν αποθηκεύτηκε προεπισκόπηση για αυτήν την έκδοση.',
    viewingEarlier: 'Βλέπετε το v{n}. Αυτή δεν είναι η έκδοση που χρησιμοποιούν οι άλλοι.',
    saveRefusedRows:
      'Οι αποθηκευμένες γραμμές δεν ταιριάζουν με αυτή την αλλαγή του πίνακα, οπότε αυτή η έκδοση δεν αποθηκεύτηκε. Οι γραμμές σας δεν άλλαξαν.',
    saveTablesPending:
      'Αυτή η έκδοση αποθηκεύτηκε, αλλά οι πίνακές της δεν έχουν αλλάξει ακόμη. Θα αλλάξουν την επόμενη φορά που θα αποθηκευτεί.',
  },
  es: {
    draftTitle: 'Borrador v{n}',
    versionTitle: 'v{n}',
    notPublished: 'No publicado',
    published: 'Publicado',
    publish: 'Publicar',
    restore: 'Restaurar…',
    basedOn: 'basado en v{n}',
    builtBy: 'creado por el creador, para {who}',
    publishedBy: 'publicado por {who}, {at}',
    builtWith: 'Creado con {model}',
    openRun: 'Abrir ejecución',
    compareTitle: 'Comparar el borrador v{to} con v{from}',
    compareHint: 'Arrastra para comparar',
    compareLabel: 'Comparar v{from} y v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Aún no hay versiones.',
    noPreview: 'No se ha guardado ninguna vista previa para esta versión.',
    viewingEarlier: 'Estás viendo la v{n}. Esta no es la versión que usa la gente.',
    saveRefusedRows:
      'Las filas guardadas no encajan con este cambio de tabla, así que no se guardó esta versión. Tus filas no han cambiado.',
    saveTablesPending:
      'Esta versión está guardada, pero sus tablas aún no han cambiado. Cambiarán la próxima vez que se guarde.',
  },
  et: {
    draftTitle: 'Mustand v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Avaldamata',
    published: 'Avaldatud',
    publish: 'Avalda',
    restore: 'Taasta…',
    basedOn: 'põhineb versioonil v{n}',
    builtBy: 'koostaja loodud kasutajale {who}',
    publishedBy: 'avaldas {who}, {at}',
    builtWith: 'Loodud mudeliga {model}',
    openRun: 'Ava käivitus',
    compareTitle: 'Võrdle mustandit v{to} versiooniga v{from}',
    compareHint: 'Võrdlemiseks lohistage',
    compareLabel: 'Võrdle versioone v{from} ja v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Versioone pole veel.',
    noPreview: 'Selle versiooni eelvaadet ei salvestatud.',
    viewingEarlier: 'Vaatate versiooni v{n}. See pole versioon, mida inimesed kasutavad.',
    saveRefusedRows:
      'Salvestatud read ei sobi selle tabelimuudatusega, seega seda versiooni ei salvestatud. Sinu read on muutumatud.',
    saveTablesPending:
      'See versioon on salvestatud, kuid selle tabelid pole veel muutunud. Need muutuvad järgmisel salvestamisel.',
  },
  fi: {
    draftTitle: 'Luonnos v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Ei julkaistu',
    published: 'Julkaistu',
    publish: 'Julkaise',
    restore: 'Palauta…',
    basedOn: 'perustuu versioon v{n}',
    builtBy: 'rakentaja rakensi, kohteelle {who}',
    publishedBy: 'julkaisija {who}, {at}',
    builtWith: 'Rakennettu mallilla {model}',
    openRun: 'Avaa suoritus',
    compareTitle: 'Vertaa luonnosta v{to} versioon v{from}',
    compareHint: 'Vertaa vetämällä',
    compareLabel: 'Vertaa versioita v{from} ja v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Versioita ei vielä ole.',
    noPreview: 'Tästä versiosta ei ole tallennettu esikatselua.',
    viewingEarlier: 'Katselet versiota v{n}. Tämä ei ole käyttäjien käyttämä versio.',
    saveRefusedRows:
      'Tallennetut rivit eivät sovi tähän taulukon muutokseen, joten tätä versiota ei tallennettu. Rivisi ovat ennallaan.',
    saveTablesPending:
      'Tämä versio on tallennettu, mutta sen taulukot eivät ole vielä muuttuneet. Ne muuttuvat seuraavan tallennuksen yhteydessä.',
  },
  fr: {
    draftTitle: 'Brouillon v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Non publié',
    published: 'Publié',
    publish: 'Publier',
    restore: 'Restaurer…',
    basedOn: 'basé sur v{n}',
    builtBy: 'créé par le concepteur, pour {who}',
    publishedBy: 'publié par {who}, {at}',
    builtWith: 'Créé avec {model}',
    openRun: 'Ouvrir l’exécution',
    compareTitle: 'Comparer le brouillon v{to} avec v{from}',
    compareHint: 'Faites glisser pour comparer',
    compareLabel: 'Comparer v{from} et v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Aucune version pour le moment.',
    noPreview: 'Aucun aperçu n’a été enregistré pour cette version.',
    viewingEarlier: 'Vous consultez v{n}. Ce n’est pas la version utilisée par les utilisateurs.',
    saveRefusedRows:
      'Les lignes enregistrées ne correspondent pas à cette modification de table : cette version n’a pas été enregistrée. Vos lignes sont inchangées.',
    saveTablesPending:
      'Cette version est enregistrée, mais ses tables n’ont pas encore changé. Elles changeront à son prochain enregistrement.',
  },
  ga: {
    draftTitle: 'Dréacht v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Gan fhoilsiú',
    published: 'Foilsithe',
    publish: 'Foilsigh',
    restore: 'Athchóirigh…',
    basedOn: 'bunaithe ar v{n}',
    builtBy: 'tógtha ag an tógálaí, do {who}',
    publishedBy: 'foilsithe ag {who}, {at}',
    builtWith: 'Tógtha le {model}',
    openRun: 'Oscail rith',
    compareTitle: 'Cuir dréacht v{to} i gcomparáid le v{from}',
    compareHint: 'Tarraing chun comparáid a dhéanamh',
    compareLabel: 'Cuir v{from} agus v{to} i gcomparáid',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Níl aon leaganacha ann fós.',
    noPreview: 'Níor sábháladh aon réamhamharc don leagan seo.',
    viewingEarlier: 'Tá tú ag féachaint ar v{n}. Ní hé seo an leagan a úsáideann daoine.',
    saveRefusedRows:
      'Ní oireann na sraitheanna sábháilte don athrú tábla seo, mar sin níor sábháladh an leagan seo. Níor athraíodh do chuid sraitheanna.',
    saveTablesPending:
      'Sábháladh an leagan seo, ach níor athraíodh a tháblaí fós. Athrófar iad an chéad uair eile a shábhálfar é.',
  },
  hr: {
    draftTitle: 'Nacrt v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Nije objavljeno',
    published: 'Objavljeno',
    publish: 'Objavi',
    restore: 'Obnovi…',
    basedOn: 'na temelju verzije v{n}',
    builtBy: 'izradio alat za izradu, za {who}',
    publishedBy: 'objavio {who}, {at}',
    builtWith: 'Izrađeno pomoću modela {model}',
    openRun: 'Otvori pokretanje',
    compareTitle: 'Usporedi nacrt v{to} s verzijom v{from}',
    compareHint: 'Povucite za usporedbu',
    compareLabel: 'Usporedi v{from} i v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Još nema verzija.',
    noPreview: 'Za ovu verziju nije spremljen pregled.',
    viewingEarlier: 'Pregledavate v{n}. To nije verzija koju ljudi upotrebljavaju.',
    saveRefusedRows:
      'Spremljeni redci ne odgovaraju ovoj promjeni tablice, pa ova verzija nije spremljena. Vaši su redci nepromijenjeni.',
    saveTablesPending:
      'Ova je verzija spremljena, ali se njezine tablice još nisu promijenile. Promijenit će se pri sljedećem spremanju.',
  },
  hu: {
    draftTitle: '{n}. vázlat',
    versionTitle: 'v{n}',
    notPublished: 'Nincs közzétéve',
    published: 'Közzétéve',
    publish: 'Közzététel',
    restore: 'Visszaállítás…',
    basedOn: 'a v{n} alapján',
    builtBy: 'a készítő készítette {who} számára',
    publishedBy: '{who} tette közzé, {at}',
    builtWith: '{model} használatával készült',
    openRun: 'Futtatás megnyitása',
    compareTitle: 'A v{to}. vázlat összehasonlítása a v{from}. verzióval',
    compareHint: 'Húzza az összehasonlításhoz',
    compareLabel: 'A v{from}. és a v{to}. összehasonlítása',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Még nincsenek verziók.',
    noPreview: 'Ehhez a verzióhoz nem lett előnézet mentve.',
    viewingEarlier: 'A v{n}. verziót tekinti meg. Ezt a verziót nem használják.',
    saveRefusedRows:
      'A mentett sorok nem illenek ehhez a táblamódosításhoz, ezért ez a verzió nem lett elmentve. A sorok változatlanok maradtak.',
    saveTablesPending:
      'Ez a verzió el van mentve, de a táblái még nem változtak. A következő mentéskor módosulnak.',
  },
  it: {
    draftTitle: 'Bozza v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Non pubblicata',
    published: 'Pubblicata',
    publish: 'Pubblica',
    restore: 'Ripristina…',
    basedOn: 'basata su v{n}',
    builtBy: 'creata dal builder, per {who}',
    publishedBy: 'pubblicata da {who}, {at}',
    builtWith: 'Creata con {model}',
    openRun: 'Apri esecuzione',
    compareTitle: 'Confronta la bozza v{to} con la v{from}',
    compareHint: 'Trascina per confrontare',
    compareLabel: 'Confronta v{from} e v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Non ci sono ancora versioni.',
    noPreview: 'Per questa versione non è stata salvata alcuna anteprima.',
    viewingEarlier: 'Stai visualizzando v{n}. Questa non è la versione utilizzata dagli utenti.',
    saveRefusedRows:
      'Le righe salvate non sono compatibili con questa modifica della tabella, quindi questa versione non è stata salvata. Le tue righe non sono cambiate.',
    saveTablesPending:
      'Questa versione è salvata, ma le sue tabelle non sono ancora cambiate. Cambieranno al prossimo salvataggio.',
  },
  lt: {
    draftTitle: 'Juodraštis v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Nepaskelbta',
    published: 'Paskelbta',
    publish: 'Skelbti',
    restore: 'Atkurti…',
    basedOn: 'pagrįsta v{n}',
    builtBy: 'sukūrė kūrimo priemonė, skirta {who}',
    publishedBy: 'paskelbė {who}, {at}',
    builtWith: 'Sukurta naudojant {model}',
    openRun: 'Atidaryti vykdymą',
    compareTitle: 'Palyginti juodraštį v{to} su v{from}',
    compareHint: 'Vilkite, kad palygintumėte',
    compareLabel: 'Palyginti v{from} ir v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Versijų dar nėra.',
    noPreview: 'Šiai versijai peržiūra neišsaugota.',
    viewingEarlier: 'Peržiūrite v{n}. Tai nėra versija, kurią naudoja žmonės.',
    saveRefusedRows:
      'Išsaugotos eilutės netinka šiam lentelės pakeitimui, todėl ši versija neišsaugota. Jūsų eilutės nepakeistos.',
    saveTablesPending:
      'Ši versija išsaugota, bet jos lentelės dar nepakeistos. Jos pasikeis kitą kartą išsaugojus.',
  },
  lv: {
    draftTitle: 'Melnraksts v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Nav publicēts',
    published: 'Publicēts',
    publish: 'Publicēt',
    restore: 'Atjaunot…',
    basedOn: 'balstīts uz v{n}',
    builtBy: 'izveidotājs, paredzēts {who}',
    publishedBy: 'publicēja {who}, {at}',
    builtWith: 'Izveidots ar {model}',
    openRun: 'Atvērt izpildi',
    compareTitle: 'Salīdzināt melnrakstu v{to} ar v{from}',
    compareHint: 'Velciet, lai salīdzinātu',
    compareLabel: 'Salīdzināt v{from} un v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Versiju vēl nav.',
    noPreview: 'Šai versijai priekšskatījums netika saglabāts.',
    viewingEarlier: 'Skatāt v{n}. Šī nav versija, ko izmanto cilvēki.',
    saveRefusedRows:
      'Saglabātās rindas neatbilst šai tabulas izmaiņai, tāpēc šī versija netika saglabāta. Jūsu rindas nav mainītas.',
    saveTablesPending:
      'Šī versija ir saglabāta, bet tās tabulas vēl nav mainītas. Tās mainīsies nākamajā saglabāšanas reizē.',
  },
  mt: {
    draftTitle: 'Abbozz v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Mhux ippubblikat',
    published: 'Ippubblikat',
    publish: 'Ippubblika',
    restore: 'Irrestawra…',
    basedOn: 'ibbażat fuq v{n}',
    builtBy: 'mibni mill-bennej, għal {who}',
    publishedBy: 'ippubblikat minn {who}, {at}',
    builtWith: 'Mibni b’{model}',
    openRun: 'Iftaħ l-eżekuzzjoni',
    compareTitle: 'Qabbel l-abbozz v{to} ma’ v{from}',
    compareHint: 'Tkaxkar biex tqabbel',
    compareLabel: 'Qabbel v{from} u v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Għad m’hemmx verżjonijiet.',
    noPreview: 'Ma ġietx salvata preview għal din il-verżjoni.',
    viewingEarlier: 'Qed tara v{n}. Din mhijiex il-verżjoni li jużaw in-nies.',
    saveRefusedRows:
      'Ir-ringieli maħżuna ma jaqblux ma’ din il-bidla fit-tabella, għalhekk din il-verżjoni ma nħażnitx. Ir-ringieli tiegħek ma nbidlux.',
    saveTablesPending:
      'Din il-verżjoni inħażnet, iżda t-tabelli tagħha għadhom ma nbidlux. Se jinbidlu fid-darba li jmiss li tinħażen.',
  },
  nl: {
    draftTitle: 'Concept v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Niet gepubliceerd',
    published: 'Gepubliceerd',
    publish: 'Publiceren',
    restore: 'Herstellen…',
    basedOn: 'gebaseerd op v{n}',
    builtBy: 'door de bouwer, voor {who}',
    publishedBy: 'gepubliceerd door {who}, {at}',
    builtWith: 'Gebouwd met {model}',
    openRun: 'Uitvoering openen',
    compareTitle: 'Concept v{to} vergelijken met v{from}',
    compareHint: 'Versleep om te vergelijken',
    compareLabel: 'v{from} en v{to} vergelijken',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Er zijn nog geen versies.',
    noPreview: 'Voor deze versie is geen voorbeeld opgeslagen.',
    viewingEarlier: 'Je bekijkt v{n}. Dit is niet de versie die mensen gebruiken.',
    saveRefusedRows:
      'Opgeslagen rijen passen niet bij deze tabelwijziging, dus deze versie is niet opgeslagen. Je rijen zijn ongewijzigd.',
    saveTablesPending:
      'Deze versie is opgeslagen, maar de tabellen zijn nog niet gewijzigd. Ze wijzigen de volgende keer dat de versie wordt opgeslagen.',
  },
  pl: {
    draftTitle: 'Wersja robocza v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Nieopublikowana',
    published: 'Opublikowana',
    publish: 'Opublikuj',
    restore: 'Przywróć…',
    basedOn: 'na podstawie v{n}',
    builtBy: 'utworzona przez kreatora dla {who}',
    publishedBy: 'opublikowana przez {who}, {at}',
    builtWith: 'Utworzona za pomocą {model}',
    openRun: 'Otwórz uruchomienie',
    compareTitle: 'Porównaj wersję roboczą v{to} z v{from}',
    compareHint: 'Przeciągnij, aby porównać',
    compareLabel: 'Porównaj v{from} i v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Nie ma jeszcze żadnych wersji.',
    noPreview: 'Dla tej wersji nie zapisano podglądu.',
    viewingEarlier: 'Wyświetlasz v{n}. To nie jest wersja używana przez użytkowników.',
    saveRefusedRows:
      'Zapisane wiersze nie pasują do tej zmiany tabeli, więc tej wersji nie zapisano. Twoje wiersze pozostały bez zmian.',
    saveTablesPending:
      'Ta wersja jest zapisana, ale jej tabele jeszcze się nie zmieniły. Zmienią się przy następnym zapisie.',
  },
  pt: {
    draftTitle: 'Rascunho v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Não publicado',
    published: 'Publicado',
    publish: 'Publicar',
    restore: 'Restaurar…',
    basedOn: 'baseado na v{n}',
    builtBy: 'criado pelo construtor, para {who}',
    publishedBy: 'publicado por {who}, {at}',
    builtWith: 'Criado com {model}',
    openRun: 'Abrir execução',
    compareTitle: 'Comparar o rascunho v{to} com a v{from}',
    compareHint: 'Arraste para comparar',
    compareLabel: 'Comparar v{from} e v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Ainda não há versões.',
    noPreview: 'Nenhuma prévia foi salva para esta versão.',
    viewingEarlier: 'Você está vendo a v{n}. Esta não é a versão usada pelas pessoas.',
    saveRefusedRows:
      'As linhas salvas não são compatíveis com esta alteração na tabela, então esta versão não foi salva. Suas linhas não foram alteradas.',
    saveTablesPending:
      'Esta versão foi salva, mas as tabelas dela ainda não foram alteradas. Elas mudarão na próxima vez que for salva.',
  },
  ro: {
    draftTitle: 'Schiță v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Nepublicată',
    published: 'Publicată',
    publish: 'Publică',
    restore: 'Restaurează…',
    basedOn: 'bazată pe v{n}',
    builtBy: 'creată de constructor, pentru {who}',
    publishedBy: 'publicată de {who}, {at}',
    builtWith: 'Creată cu {model}',
    openRun: 'Deschide rularea',
    compareTitle: 'Compară schița v{to} cu v{from}',
    compareHint: 'Trage pentru a compara',
    compareLabel: 'Compară v{from} și v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Nu există încă versiuni.',
    noPreview: 'Nu a fost salvată nicio previzualizare pentru această versiune.',
    viewingEarlier: 'Vizualizezi v{n}. Aceasta nu este versiunea folosită de oameni.',
    saveRefusedRows:
      'Rândurile salvate nu se potrivesc cu această modificare a tabelului, deci versiunea nu a fost salvată. Rândurile tale sunt neschimbate.',
    saveTablesPending:
      'Această versiune este salvată, dar tabelele ei nu s-au schimbat încă. Se vor schimba la următoarea salvare.',
  },
  sk: {
    draftTitle: 'Koncept v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Nepublikované',
    published: 'Publikované',
    publish: 'Publikovať',
    restore: 'Obnoviť…',
    basedOn: 'založené na v{n}',
    builtBy: 'vytvoril tvorca pre {who}',
    publishedBy: 'publikoval {who}, {at}',
    builtWith: 'Vytvorené pomocou {model}',
    openRun: 'Otvoriť spustenie',
    compareTitle: 'Porovnať koncept v{to} s v{from}',
    compareHint: 'Potiahnutím porovnáte',
    compareLabel: 'Porovnať v{from} a v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Zatiaľ nemáte žiadne verzie.',
    noPreview: 'Pre túto verziu sa neuložil žiadny náhľad.',
    viewingEarlier: 'Zobrazujete v{n}. Toto nie je verzia, ktorú ľudia používajú.',
    saveRefusedRows:
      'Uložené riadky nezodpovedajú tejto zmene tabuľky, preto sa táto verzia neuložila. Vaše riadky zostali bez zmeny.',
    saveTablesPending:
      'Táto verzia je uložená, ale jej tabuľky sa zatiaľ nezmenili. Zmenia sa pri ďalšom uložení.',
  },
  sl: {
    draftTitle: 'Osnutek v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Ni objavljeno',
    published: 'Objavljeno',
    publish: 'Objavi',
    restore: 'Obnovi…',
    basedOn: 'na podlagi v{n}',
    builtBy: 'ustvaril graditelj za {who}',
    publishedBy: 'objavil {who}, {at}',
    builtWith: 'Ustvarjeno z {model}',
    openRun: 'Odpri izvajanje',
    compareTitle: 'Primerjaj osnutek v{to} z v{from}',
    compareHint: 'Povlecite za primerjavo',
    compareLabel: 'Primerjaj v{from} in v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Različic še ni.',
    noPreview: 'Za to različico predogled ni bil shranjen.',
    viewingEarlier: 'Ogledate si v{n}. To ni različica, ki jo ljudje uporabljajo.',
    saveRefusedRows:
      'Shranjene vrstice se ne ujemajo s to spremembo tabele, zato ta različica ni bila shranjena. Vaše vrstice so nespremenjene.',
    saveTablesPending:
      'Ta različica je shranjena, a njene tabele se še niso spremenile. Spremenile se bodo ob naslednjem shranjevanju.',
  },
  sv: {
    draftTitle: 'Utkast v{n}',
    versionTitle: 'v{n}',
    notPublished: 'Inte publicerad',
    published: 'Publicerad',
    publish: 'Publicera',
    restore: 'Återställ…',
    basedOn: 'baserad på v{n}',
    builtBy: 'av byggaren, åt {who}',
    publishedBy: 'publicerad av {who}, {at}',
    builtWith: 'Byggd med {model}',
    openRun: 'Öppna körning',
    compareTitle: 'Jämför utkast v{to} med v{from}',
    compareHint: 'Dra för att jämföra',
    compareLabel: 'Jämför v{from} och v{to}',
    compareFrom: 'v{n}',
    compareTo: 'v{n}',
    empty: 'Inga versioner än.',
    noPreview: 'Ingen förhandsvisning sparades för den här versionen.',
    viewingEarlier: 'Du tittar på v{n}. Det är inte den version folk använder.',
    saveRefusedRows:
      'Sparade rader passar inte den här tabelländringen, så versionen sparades inte. Dina rader är oförändrade.',
    saveTablesPending:
      'Versionen är sparad, men tabellerna har inte ändrats än. De ändras nästa gång den sparas.',
  },
};
export default appVersionsCopy;
